/*
 * Shared cancellable/mount-safe run scaffold for Deckorate's AI image
 * features (Sketch to Slide, Photo Headshot, ...). Each feature's `run()`
 * keeps its own capture/upload/analyze/compose sequence and phase-by-phase
 * `setState` calls — only the reentrancy guard, abort/timeout wiring, and
 * unmount-safety boilerplate around it lives here.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { isAbortError } from "../services/connectorErrors";

export interface AbortableRunRefs {
  readonly isRunningRef: React.MutableRefObject<boolean>;
  readonly abortRef: React.MutableRefObject<AbortController | null>;
  readonly timedOutRef: React.MutableRefObject<boolean>;
}

export interface AbortableRunControls<TState> extends AbortableRunRefs {
  readonly state: TState;
  readonly safeSetState: (next: TState) => void;
  readonly cancel: () => void;
  readonly reset: () => void;
}

/** Mount-safe state plus the refs a feature's `run()` needs to wire up
 *  `runAbortable` — reentrancy guard, in-flight AbortController, and
 *  whether the last abort was a timeout rather than a user cancel. */
export function useAbortableRun<TState>(idleState: TState): AbortableRunControls<TState> {
  const [state, setState] = useState<TState>(idleState);

  const isRunningRef = useRef(false);
  const isMountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  const timedOutRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const safeSetState = useCallback((next: TState) => {
    if (isMountedRef.current) setState(next);
  }, []);

  const cancel = useCallback(() => {
    timedOutRef.current = false;
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => safeSetState(idleState), [safeSetState, idleState]);

  return { state, safeSetState, isRunningRef, abortRef, timedOutRef, cancel, reset };
}

export type AbortableRunOutcome<T> =
  | { readonly kind: "skipped" }
  | { readonly kind: "ok"; readonly value: T }
  | { readonly kind: "cancelled" }
  | { readonly kind: "failed"; readonly error: unknown; readonly timedOut: boolean };

/**
 * Runs `body` under a fresh AbortController that auto-aborts after
 * `timeoutMs`, guarding against re-entrancy via `refs.isRunningRef`. Mirrors
 * each feature's previous inline try/catch/finally exactly: a user-initiated
 * abort (not a timeout) reports as "cancelled"; anything else (including a
 * timeout abort) reports as "failed" with `timedOut` for the caller's
 * describeXFailure(..., { timedOut }) copy.
 */
export async function runAbortable<T>(
  refs: AbortableRunRefs,
  timeoutMs: number,
  body: (signal: AbortSignal) => Promise<T>,
): Promise<AbortableRunOutcome<T>> {
  if (refs.isRunningRef.current) return { kind: "skipped" };

  refs.isRunningRef.current = true;
  refs.timedOutRef.current = false;

  const controller = new AbortController();
  refs.abortRef.current = controller;
  const timeoutId = setTimeout(() => {
    refs.timedOutRef.current = true;
    controller.abort();
  }, timeoutMs);

  try {
    const value = await body(controller.signal);
    return { kind: "ok", value };
  } catch (error) {
    const timedOut = refs.timedOutRef.current;
    if (isAbortError(error) && !timedOut) return { kind: "cancelled" };
    return { kind: "failed", error, timedOut };
  } finally {
    clearTimeout(timeoutId);
    if (refs.abortRef.current === controller) refs.abortRef.current = null;
    refs.isRunningRef.current = false;
  }
}
